import React, { useMemo, useState } from "react";
import { X, CalendarPlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DateInput } from "@/components/DateInput";

const TUR_META = {
  ucretli_izin:  { label: "Ücretli İzin",  color: "indigo", desc: "Yıllık izin hakkından düşülür. Ücret ödenir." },
  rapor:         { label: "Rapor",         color: "rose",   desc: "Sağlık raporu. Yıllık izinden düşülmez." },
  ucretsiz_izin: { label: "Ücretsiz İzin", color: "amber",  desc: "Ücret ödenmez. Yıllık izinden düşülmez." },
  yillik:        { label: "Yıllık İzin",   color: "emerald",desc: "Yıllık izin hakkından düşülür." },
};

/**
 * Mini date-range form that creates a new annual_leave record pre-filled
 * with the clicked personnel + tur. Used by PayrollTable to let the user
 * enter paid leave / rapor / unpaid leave as a date range directly from
 * the bordro cetveli — the record then shows up in both Bordro (as a
 * `+N yıllık` chip) and the Yıllık İzinler screen.
 */
export default function LeaveQuickAddModal({
  personnel, // { id, ad_soyad }
  tur,       // "ucretli_izin" | "rapor" | "ucretsiz_izin"
  yil, ay,   // suggested month context — only used to prefill a sensible default start date
  onClose,
  onSaved,
}) {
  const meta = TUR_META[tur] || TUR_META.ucretli_izin;
  // Default: suggested_start = first weekday of selected month if in past, else today
  const defaultStart = useMemo(() => {
    const d = new Date();
    if (yil && ay) {
      const m = `${yil}-${String(ay).padStart(2, "0")}-01`;
      return m;
    }
    return d.toISOString().slice(0, 10);
  }, [yil, ay]);
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultStart);
  const [isGunu, setIsGunu] = useState(tur === "rapor" ? false : true);
  const [aciklama, setAciklama] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!start || !end) { toast.error("Tarih seçin"); return; }
    if (end < start) { toast.error("Bitiş, başlangıçtan önce olamaz"); return; }
    setBusy(true);
    try {
      await api.post("/annual-leave", {
        personnel_id: personnel.id,
        baslangic: start,
        bitis: end,
        tur,
        is_gunu_modu: isGunu,
        aciklama: (aciklama || "").trim(),
      });
      toast.success(`${personnel.ad_soyad} · ${meta.label} eklendi`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const tone = {
    indigo:  { bg: "bg-indigo-50",  bd: "border-indigo-200",  tx: "text-indigo-900",  btn: "bg-indigo-600 hover:bg-indigo-700" },
    rose:    { bg: "bg-rose-50",    bd: "border-rose-200",    tx: "text-rose-900",    btn: "bg-rose-600 hover:bg-rose-700" },
    amber:   { bg: "bg-amber-50",   bd: "border-amber-200",   tx: "text-amber-900",   btn: "bg-amber-600 hover:bg-amber-700" },
    emerald: { bg: "bg-emerald-50", bd: "border-emerald-200", tx: "text-emerald-900", btn: "bg-emerald-600 hover:bg-emerald-700" },
  }[meta.color];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="leave-quick-add-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2">
            <CalendarPlus size={16} className={tone.tx}/>
            {meta.label} Ekle
          </h3>
          <button onClick={onClose} className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14}/></button>
        </div>

        <div className="p-4 space-y-3">
          <div className={`${tone.bg} border ${tone.bd} rounded-md px-3 py-2`}>
            <div className={`font-bold ${tone.tx} text-sm`}>{personnel.ad_soyad}</div>
            <div className="text-xs text-slate-600 mt-0.5">{meta.desc}</div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Başlangıç</label>
              <DateInput
                value={start}
                onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm font-mono"
                data-testid="leave-quick-start"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Bitiş</label>
              <DateInput
                value={end}
                min={start}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm font-mono"
                data-testid="leave-quick-end"
              />
            </div>
          </div>

          <label className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2 cursor-pointer text-xs select-none">
            <input
              type="checkbox"
              checked={isGunu}
              onChange={(e) => setIsGunu(e.target.checked)}
              className="mt-0.5"
              data-testid="leave-quick-isgunu"
            />
            <div className="flex-1">
              <strong>İş günü hesabı</strong>
              <div className="text-slate-500 mt-0.5">Hafta sonu ve resmi tatiller düşülerek sayılır.</div>
            </div>
          </label>

          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">Açıklama <span className="text-slate-400">(opsiyonel)</span></label>
            <input
              type="text"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              placeholder="Örn: Aile ziyareti"
              className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="leave-quick-aciklama"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">İptal</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="leave-quick-submit"
            className={`px-4 py-2 text-sm font-semibold text-white rounded-md disabled:opacity-50 inline-flex items-center gap-1.5 ${tone.btn}`}
          >
            <CheckCircle2 size={14}/>
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}
